import formatPropertyName from "utils/column/formatPropertyName";
import getGroupByListColumnByProperty from "utils/column/getGroupByListColumnByProperty";
import getProductDisplayName from "utils/data-table/getProductDisplayName";

import { MlFeature } from "../types";

/**
 * Gets the display name for an ML feature with units appended based on the user's unit preference
 * @param feature - The ML feature to get the display name for
 * @param unitType - The user's unit preference (e.g., "imperial", "si")
 * @returns The formatted display name with product prefix (if applicable) and units
 */
export function getFeatureDisplayNameWithProductAndUnits(
  feature: MlFeature,
  unitType: number
): string {
  const baseName = feature.NiceName || feature.Property;
  const unit = feature.unit?.[unitType] || "";
  const unitSuffix = unit && !baseName.includes(`(${unit})`) ? ` (${unit})` : "";
  const nameWithUnits = `${baseName}${unitSuffix}`;

  return feature.product
    ? `${getProductDisplayName(feature.product)}: ${nameWithUnits}`
    : nameWithUnits;
}

/**
 * Removes isolated parentheses from the end of a string (e.g., unit notations)
 * Only removes parentheses that are preceded by whitespace (not part of the text like "(O+C)GR")
 * For template display
 * @param text - The text to process
 * @returns The text with isolated trailing parentheses removed
 * @example
 * removeIsolatedTrailingParentheses("Feature Name (bbl)") // "Feature Name"
 * removeIsolatedTrailingParentheses("(O+C)GR") // "(O+C)GR"
 * removeIsolatedTrailingParentheses("Value (ft)") // "Value"
 */
export function removeIsolatedTrailingParentheses(text: string): string {
  // Match parentheses that are preceded by whitespace and followed by end of string
  const lastParenMatch = text.match(/\s+\([^)]*\)\s*$/);
  if (lastParenMatch) {
    return text.replace(lastParenMatch[0], "").trim();
  }
  return text;
}

/**
 * Resolves the display unit for a normalization feature using a 3-level fallback:
 * NormalizeBy.Unit → NormalizeBy.unit[unitType] → categoryList lookup.
 *
 * @param normalizeBy - The NormalizeBy feature (MlFeature with optional Unit)
 * @param unitType - The user's unit preference index
 * @param categoryList - The groupBy category list from Redux state
 * @returns The resolved unit string, or empty string if none found
 */
export function resolveNormalizationUnit(
  normalizeBy: (MlFeature & { Unit?: string }) | null | undefined,
  unitType: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  categoryList: any[]
): string {
  if (!normalizeBy) return "";
  return (
    normalizeBy.Unit ||
    normalizeBy.unit?.[unitType] ||
    getGroupByListColumnByProperty(categoryList, formatPropertyName(normalizeBy.Property))
      ?.units?.[unitType] ||
    ""
  );
}

/**
 * Builds the "Normalized by Name (per value unit)" label text for a given normalization entry.
 * Returns undefined when there is no normalization applied.
 *
 * @param normalizeBy - The NormalizeBy feature, or null/undefined if not normalized
 * @param unitType - The user's unit preference index
 * @param categoryList - The groupBy category list from Redux state
 * @returns Formatted label string, or undefined if no normalization
 */
export function getNormalizedByLabel(
  normalizeBy: (MlFeature & { Unit?: string; Value?: number }) | null | undefined,
  unitType: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  categoryList: any[]
): string | undefined {
  if (!normalizeBy) return undefined;
  const unit = resolveNormalizationUnit(normalizeBy, unitType, categoryList);
  const perValue = normalizeBy.Value;
  const unitLabel = [perValue != null ? String(perValue) : "", unit]
    .filter(Boolean)
    .join(" ");
  const niceName = removeIsolatedTrailingParentheses(normalizeBy.NiceName);
  return `Normalized by ${niceName}${unitLabel ? ` (${unitLabel})` : ""}`;
}
